import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import axios from 'axios';
import { isEmpty } from 'lodash';

import { ClaimSearchResults } from '~/components/ClaimSearch';
import CliveConditionalDisplay from '~/components/Clive/CliveConditionalDisplay';
import mixpanel from '~/components/CmsMain/mixpanel';
import { markCommunicationAsRead } from '~/components/communications/utils/markCommunicationAsRead';
import ViewCommunicationCardContainer from '~/components/communications/ViewCommunicationCardContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import type { InboxFilterState } from '~/components/Fnol/FnolAiInbox/FnolAiInboxContainer';
import { useFnolAiInboxPagination } from '~/components/Fnol/FnolAiInbox/hooks/useFnolAiInboxPagination';
import { PolicySelectionCard } from '~/components/Fnol/FnolAiInbox/PolicySelectionCard/PolicySelectionCard';
import FnolAiInboxTable from '~/components/Fnol/FnolAiInbox/table/FnolAiInboxTable';
import { determineFnolRecommendationState } from '~/components/Fnol/FnolAiInbox/tabs/fnolAiRecommendationStateUtils';
import { FnolInboxItemAiAnalysisBanner } from '~/components/Fnol/FnolAiInbox/tabs/FnolInboxItemAiAnalysisBanner';
import type {
  FnolDraft,
  InboxType,
  OnNewLossCallback,
  Policy,
  PolicySearchValues,
} from '~/components/Fnol/FnolAiInbox/types';
import { FnolRecommendationState } from '~/components/Fnol/FnolAiInbox/types';
import { DISABLED_STATUSES_SET } from '~/components/Fnol/FnolAiInbox/utils';
import { useCms } from '~/components/hooks/useCms';
import LoadingDialog from '~/components/LoadingDialog';
import LoadingIndicator from '~/components/LoadingIndicator';
import useOrganization from '~/components/OrganizationContext';
import type { SelectedPolicyItem } from '~/components/PolicySearchV2';
import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import useDebouncedValue from '~/hooks/useDebouncedValue';
import { FNOL_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import type {
  ClaimsDisplayRetClaimDisplayEntry,
  FnolInboxFeContextResponse,
  FnolInboxRecord,
} from '~/server_shared/openapi/data-contracts';
import { FnolInboxRecordFnolInboxStatus } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';

import { useShouldDisplayCliveForInboxRecord } from '../hooks/useShouldDisplayCliveForInboxRecord';
import { ReassignInboxDialog } from '../ReassignInboxDialog/ReassignInboxDialog';

const DEFAULT_ROWS_PER_PAGE = 10;
const FILTER_DEBOUNCE_MS = 250;
const SKELETON_COLUMNS_COUNT = 6;

interface FnolAiInboxTabsLayoutProps {
  inboxType: InboxType;
  onNewLoss?: ({ fnolDraft, communicationId, fnolAiRecommendationId, subOrgId, lob }: OnNewLossCallback) => void;
  filters?: InboxFilterState;
}

export const FnolAiInboxTab: React.FC<FnolAiInboxTabsLayoutProps> = ({ inboxType, onNewLoss, filters }) => {
  const { user } = useCms();
  const { isPolicyAPIEnabled }: { isPolicyAPIEnabled?: boolean } = useOrganization();
  const { isCliveEnabled, isCliveEnabledForOrganization } = useCliveConfigOptions();
  const isCliveEnabledForOrg = isCliveEnabledForOrganization();
  const shouldDisplayCliveForRow = useShouldDisplayCliveForInboxRecord();

  // Sort state management
  const [sortByColumn, setSortByColumn] = React.useState<{ id: string; order: 'asc' | 'desc' }>({
    id: 'date_received',
    order: 'desc',
  });

  const sortString = `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`;

  // Debounce the full filters object so rapid multiselect toggles don't fire one fetch per click.
  const debouncedFilters = useDebouncedValue(filters, FILTER_DEBOUNCE_MS);

  const {
    pageResults: inboxItems,
    isLoading,
    isError,
    reload: reloadInboxItems,
    refreshPageData: refreshInboxItems,
    pagination,
  } = useFnolAiInboxPagination({
    inboxType,
    sortByColumn: sortString,
    initialRowsPerPage: DEFAULT_ROWS_PER_PAGE,
    subOrganizationIds: debouncedFilters?.subOrgIds,
    includeUnknownSubOrganization: debouncedFilters?.includeUnknownSubOrganization,
    lobs: debouncedFilters?.lobs,
    includeUnknownLob: debouncedFilters?.includeUnknownLob,
    withReadStatus: true,
  });

  const [selectedInboxItem, setSelectedInboxItem] = React.useState<FnolInboxRecord>();
  const [showAttachToClaim, setShowAttachToClaim] = React.useState(false);
  const [editPolicyInboxItem, setEditPolicyInboxItem] = React.useState<FnolInboxRecord | undefined>();
  const [relatedClaims, setRelatedClaims] = React.useState<ClaimsDisplayRetClaimDisplayEntry[]>([]);
  const [showReassignInboxDialog, setShowReassignInboxDialog] = React.useState(false);
  const [isCreatingFnol, setIsCreatingFnol] = React.useState(false);

  // Reset reassign dialog flag if selectedInboxItem is cleared by other means
  React.useEffect(() => {
    if (!selectedInboxItem) {
      setShowReassignInboxDialog(false);
    }
  }, [selectedInboxItem]);

  const toggleSelectedInboxItem = React.useCallback(
    (row: FnolInboxRecord) => {
      const unselect = selectedInboxItem?.id === row.id;
      const newVal = unselect ? undefined : row;
      setSelectedInboxItem(newVal);

      // Auto-mark as read when selecting an item (and it's not already read)
      if (!unselect && row.is_read !== true) {
        // Fire and forget - handle async operation without blocking
        markCommunicationAsRead({
          communicationId: row.email_communication_id,
          user,
          onRefresh: async () => {
            await refreshInboxItems();
          },
          throwOnError: false, // Handle errors silently for non-critical functionality
        });
      }
    },
    [selectedInboxItem, refreshInboxItems, user]
  );

  // Re-point the selection at the refreshed row; otherwise the detail pane keeps rendering the copy
  // captured before the reload.
  const refreshInboxItemsAndSelection = React.useCallback(async () => {
    const updatedItems = await refreshInboxItems();

    if (selectedInboxItem?.id) {
      const updatedSelectedItem = updatedItems.find((item) => item.id === selectedInboxItem?.id);
      if (updatedSelectedItem) {
        setSelectedInboxItem(updatedSelectedItem);
      }
    }
  }, [refreshInboxItems, selectedInboxItem?.id]);

  // Policy selection logic
  const onPolicySelectionCancel = React.useCallback(() => {
    setEditPolicyInboxItem(undefined);
  }, []);

  const onPolicySelectionCancelWithRefresh = React.useCallback(async () => {
    if (editPolicyInboxItem?.is_clive_enabled_for_incident_configuration === true) {
      await refreshInboxItemsAndSelection();
      setEditPolicyInboxItem(undefined);
    }
  }, [editPolicyInboxItem, refreshInboxItemsAndSelection]);

  const onPolicySelection = React.useCallback((inboxItem: FnolInboxRecord) => {
    const disableDialog = !inboxItem.policy && DISABLED_STATUSES_SET.has(inboxItem.status);
    if (disableDialog) return;

    setEditPolicyInboxItem(inboxItem);
  }, []);

  const policySearchValues = React.useMemo<PolicySearchValues>(
    () => ({
      sub_org_id: editPolicyInboxItem?.sub_organization_id,
      lob: editPolicyInboxItem?.line_of_business,
    }),
    [editPolicyInboxItem]
  );

  const handleSort = React.useCallback(
    (columnId: string, order: 'asc' | 'desc') => {
      // Only allow sorting by date_received as per requirements
      if (columnId === 'date_received') {
        setSortByColumn({ id: columnId, order });
        pagination.setPage(0); // Reset to first page when sorting changes
      }
      // For other columns, we could show a message or just ignore
      // For now, we'll just ignore non-date_received sorts
    },
    [pagination]
  );

  // Create pagination props for the table
  const paginationProps = React.useMemo(
    () => ({
      page: pagination.page,
      count: pagination.totalCount,
      rowsPerPage: pagination.rowsPerPage,
      onChangePage: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        pagination.setPage(page);
      },
      onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
        pagination.setRowsPerPage(parseInt(event.target.value, 10));
      },
      rowsPerPageOptions: [10, 25, 50, 100],
    }),
    [pagination]
  );

  const fnolWithClive: boolean | null = React.useMemo(() => {
    if (!selectedInboxItem) {
      return null;
    }
    if (selectedInboxItem.status === FnolInboxRecordFnolInboxStatus.AiProcessingFailed) {
      return false;
    }

    // Check sub-organization specific Clive configuration if sub_organization_id exists
    if (selectedInboxItem.effective_sub_org_id) {
      return isCliveEnabled(selectedInboxItem.effective_sub_org_id);
    }

    // Fall back to organization-level configuration
    return isCliveEnabledForOrg;
  }, [isCliveEnabled, isCliveEnabledForOrg, selectedInboxItem]);

  // Calculate if "Create New Loss" button should be disabled
  const isCreateNewLossDisabled = React.useMemo(() => {
    if (inboxType !== 'in-queue') {
      return false;
    }

    const allowedStatuses = [
      FnolInboxRecordFnolInboxStatus.AiNew,
      FnolInboxRecordFnolInboxStatus.AiProcessingFailed,
      FnolInboxRecordFnolInboxStatus.New,
    ];

    return !selectedInboxItem || !allowedStatuses.includes(selectedInboxItem.status);
  }, [selectedInboxItem, inboxType]);

  const proceedWithFnolCreation = React.useCallback(async () => {
    if (!selectedInboxItem || !onNewLoss) return;

    // Extract AI metadata if conditions are met (same as Mixpanel event conditions)
    let mixpanelExtra = null;
    if (selectedInboxItem.was_sent_to_ai_service && selectedInboxItem.analysis_extra) {
      mixpanelExtra = {
        fnol_inbox_item_id: selectedInboxItem.id,
        has_related_claims: Boolean(selectedInboxItem.analysis_extra?.related_claims?.length),
        related_claims_count: selectedInboxItem.analysis_extra?.related_claims?.length || 0,
        organization_id: selectedInboxItem.organization_id,
        sub_organization_id: selectedInboxItem.sub_organization_id!,
        line_of_business: selectedInboxItem.line_of_business!,
        email_communication_id: selectedInboxItem.email_communication_id,
      };

      // Fire Mixpanel event for FNOL creation from inbox with Clive AI recommendations
      // This event tracks when users create FNOLs from AI-processed inbox items to measure
      // the effectiveness of AI recommendations in the FNOL creation workflow
      mixpanel.track(FNOL_MIXPANEL_EVENTS.REPORT_A_NEW_LOSS_CLICKED_WITH_CLIVE_AI_RECOMMENDATION_FNOL, mixpanelExtra);
    }

    try {
      setIsCreatingFnol(true);

      const response = await axios.get<FnolInboxFeContextResponse>(
        `/api/v1/fnol_inbox/inbox/${selectedInboxItem.id}/fnol_fe_context`
      );

      const { fnol_draft, policy } = response.data;

      // Check if we have AI recommendations using the same logic as the analysis banner
      const recommendationState = determineFnolRecommendationState(selectedInboxItem);
      const hasAiRecommendations = recommendationState === FnolRecommendationState.RECOMMENDATION_EXISTS;

      const newLossData: OnNewLossCallback = {
        fnolDraft: fnol_draft as FnolDraft | undefined,
        policyData: policy as Policy | undefined,
        communicationId: selectedInboxItem.email_communication_id,
        fnolAiRecommendationId: selectedInboxItem.id,
        lob: selectedInboxItem.line_of_business || null,
        subOrgId: selectedInboxItem.sub_organization_id || null,
        mixpanelExtra,
        hasAiRecommendations,
        isCliveEnabled: selectedInboxItem.effective_sub_org_id
          ? isCliveEnabled(selectedInboxItem.effective_sub_org_id)
          : isCliveEnabledForOrg,
      };

      onNewLoss(newLossData);
    } catch (error) {
      await reportAxiosError(error);
      setIsCreatingFnol(false);
    }
  }, [selectedInboxItem, onNewLoss, isCliveEnabled, isCliveEnabledForOrg]);

  const onCreateNewLoss = React.useCallback(async () => {
    if (!selectedInboxItem || !onNewLoss) return;

    if (isCreateNewLossDisabled) {
      return;
    }

    // While the button is disabled, this check is an extra safeguard.
    // The policy selection updates the inbox item with the correct sub_organization_id and lob.
    if (selectedInboxItem.sub_organization_id === undefined || selectedInboxItem.line_of_business === undefined) {
      reportErrorInProductionOrThrow('Selected inbox item is missing sub_organization_id or line_of_business');
      return;
    }

    // Check for existing claims on the same policy before proceeding
    const policyNumber = selectedInboxItem.policy?.policy_number;
    if (policyNumber) {
      try {
        const claimsRes = await axios.get('/api/v1/claims', {
          params: { policy_number: policyNumber },
        });
        const existingClaims = claimsRes.data.claims;

        if (existingClaims.length > 0) {
          setRelatedClaims(existingClaims);
          return;
        }
      } catch (claimsError) {
        await reportAxiosError(claimsError);
        // Non-blocking: proceed to create FNOL even if claims check fails
      }
    }

    await proceedWithFnolCreation();
  }, [selectedInboxItem, onNewLoss, isCreateNewLossDisabled, proceedWithFnolCreation]);

  const handleProceedDespiteDuplicates = React.useCallback(async () => {
    setRelatedClaims([]);
    await proceedWithFnolCreation();
  }, [proceedWithFnolCreation]);

  const handleCancelDuplicateCheck = React.useCallback(() => {
    setRelatedClaims([]);
  }, []);

  const onCreateNewLossWithPolicySelection = React.useCallback(() => {
    if (!selectedInboxItem || !onNewLoss) return;

    onPolicySelection(selectedInboxItem);
  }, [selectedInboxItem, onPolicySelection, onNewLoss]);

  const handleCreateNewLoss = React.useCallback(() => {
    if (fnolWithClive) {
      onCreateNewLoss();
    } else {
      onCreateNewLossWithPolicySelection();
    }
  }, [fnolWithClive, onCreateNewLoss, onCreateNewLossWithPolicySelection]);

  const MENU_ITEMS = {
    dismiss: {
      onClick: () => onDismissClick(),
      description: 'Dismiss',
      shouldDisplay: true,
    },
    createNewLoss: {
      onClick: handleCreateNewLoss,
      description: 'Create FNOL',
      shouldDisplay: true,
      disabled: isCreateNewLossDisabled,
      tooltipText: isCreateNewLossDisabled ? 'Please select a policy to create FNOL' : undefined,
    },
    openBack: {
      onClick: () => onOpenBack(),
      description: 'Re-Open',
      shouldDisplay: true,
    },
    moveToGeneralQueue: {
      onClick: () => onMoveToGeneralQueue(),
      description: 'Move to General Queue',
      shouldDisplay: true,
      disabled: !selectedInboxItem?.can_move_to_gq,
      tooltipText: selectedInboxItem?.can_move_to_gq
        ? undefined
        : 'This email domain has no general mail classification queue',
    },
    reassignInbox: {
      onClick: () => setShowReassignInboxDialog(true),
      description: 'Reassign to another Inbox',
      shouldDisplay: true,
    },
  };

  const onDismissClick = React.useCallback(async () => {
    if (!selectedInboxItem) return;
    try {
      await axios.post<FnolDraft>(`/api/v1/fnol_inbox/inbox/${selectedInboxItem.id}/dismiss`);
      await reloadInboxItems();
      setSelectedInboxItem(undefined);
    } catch (error) {
      await reportAxiosError(error);
    }
  }, [selectedInboxItem, reloadInboxItems]);

  const onMoveToGeneralQueue = React.useCallback(async () => {
    if (!selectedInboxItem) return;
    try {
      await axios.post(`/api/v1/fnol_inbox/inbox/${selectedInboxItem.id}/move_to_gq`);
      await reloadInboxItems();
      setSelectedInboxItem(undefined);
    } catch (error) {
      await reportAxiosError(error);
    }
  }, [selectedInboxItem, reloadInboxItems]);

  const onOpenBack = React.useCallback(async () => {
    if (!selectedInboxItem) return;
    try {
      await axios.post<FnolDraft>(`/api/v1/fnol_inbox/inbox/${selectedInboxItem.id}/re_open`);
      setSelectedInboxItem(undefined);
      await reloadInboxItems();
    } catch (error) {
      await reportAxiosError(error);
    }
  }, [selectedInboxItem, reloadInboxItems]);

  const onInboxReassigned = React.useCallback(async () => {
    setSelectedInboxItem(undefined);
    await reloadInboxItems();
  }, [reloadInboxItems]);

  const onRetryProcessing = React.useCallback(
    async (inboxItemId: number) => {
      try {
        await axios.post(`/api/v1/fnol_inbox/inbox/${inboxItemId}/retry`);
        const updatedItems = await refreshInboxItems();

        // If there's a selected item, find it in the updated data and update the selection
        if (selectedInboxItem?.id === inboxItemId) {
          const updatedSelectedItem = updatedItems.find((item) => item.id === inboxItemId);
          if (updatedSelectedItem) {
            setSelectedInboxItem(updatedSelectedItem);
          }
        }
      } catch (error) {
        await reportAxiosError(error);
      }
    },
    [refreshInboxItems, selectedInboxItem?.id]
  );

  // CASE 3: "Take Loss Manually" — open empty FNOL form with email side panel
  const handleTakeLossManually = React.useCallback(() => {
    if (!selectedInboxItem || !onNewLoss) return;

    onNewLoss({
      fnolDraft: undefined,
      policyData: undefined,
      communicationId: selectedInboxItem.email_communication_id,
      fnolAiRecommendationId: selectedInboxItem.id,
      lob: selectedInboxItem.line_of_business || null,
      subOrgId: selectedInboxItem.sub_organization_id || null,
    });
  }, [selectedInboxItem, onNewLoss]);

  const onCloseCommunicationCard = React.useCallback(() => {
    setSelectedInboxItem(undefined);
    setShowAttachToClaim(false);
    void reloadInboxItems();
  }, [reloadInboxItems]);

  const handleAttachToExistingClaim = async (claim_id: number, attachExtraParams: Record<string, unknown>) => {
    if (!selectedInboxItem) return;
    try {
      await axios.post(`/api/v1/fnol_inbox/inbox/${selectedInboxItem.id}/attach_to_existing_claim`, {
        claim_id,
        ...attachExtraParams,
      });
      setShowAttachToClaim(false);
      setSelectedInboxItem(undefined);
      await reloadInboxItems();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const handlePolicyChange = React.useCallback(
    async (
      inboxItemId: number,
      chosenPolicyData?: Policy | SelectedPolicyItem,
      shouldShowChangePolicyMessage?: boolean,
      withClive?: boolean | null
    ) => {
      if (!inboxItemId) return;

      try {
        if (withClive) {
          await axios.patch(`/api/v1/fnol_inbox/inbox/${inboxItemId}`, {
            selected_policy: chosenPolicyData ?? null,
          });

          // Only reload immediately if the dialog should not stay open
          // If dialog should stay open, refresh will happen when user closes it manually
          if (!shouldShowChangePolicyMessage) {
            await refreshInboxItemsAndSelection();

            // Close dialog only when shouldShowChangePolicyMessage is false
            setEditPolicyInboxItem(undefined);
          }
          // If shouldShowChangePolicyMessage is true, dialog stays open for user confirmation
        } else if (onNewLoss && editPolicyInboxItem) {
          // If we're in create FNOL mode, use format_policy endpoint to get formatted policy data
          const formatPolicyResponse = await axios.post<{
            policy_data: Policy | null;
          }>(`/api/v1/fnol_inbox/inbox/${editPolicyInboxItem.id}/format_policy`, {
            selected_policy: chosenPolicyData ?? null,
          });

          const { policy_data } = formatPolicyResponse.data;

          // Call onNewLoss directly with formatted policy data
          onNewLoss({
            fnolDraft: undefined, // No draft in this flow
            policyData: policy_data || undefined,
            communicationId: editPolicyInboxItem.email_communication_id,
            fnolAiRecommendationId: editPolicyInboxItem.id,
            lob: null, // Not used by onNewLoss
            subOrgId: null, // Not used by onNewLoss
            isCliveEnabled: isCliveEnabled(editPolicyInboxItem.sub_organization_id),
          });

          // Close dialog after successful FNOL creation navigation
          setEditPolicyInboxItem(undefined);
        }
      } catch (error) {
        await reportAxiosError(error);
        // Re-throw error to allow calling components to handle failures
        throw error;
      }
    },
    [onNewLoss, editPolicyInboxItem, refreshInboxItemsAndSelection, isCliveEnabled]
  );

  function getMenuActions() {
    if (!selectedInboxItem) return [];

    if (inboxType === 'in-queue') {
      const menuActions = [];
      menuActions.push(MENU_ITEMS['moveToGeneralQueue']);
      menuActions.push(MENU_ITEMS['reassignInbox']);
      menuActions.push(MENU_ITEMS['dismiss']);
      menuActions.push(MENU_ITEMS['createNewLoss']);
      return menuActions;
    }

    if (inboxType === 'done' && selectedInboxItem.status === 'dismissed') {
      return [MENU_ITEMS['openBack']];
    }

    return [];
  }

  return (
    <PanelGroup direction="horizontal" id="group-claim-communications">
      <Panel id="fnol-inbox-table-display" defaultSize={70} className="max-h-[75vh]">
        <>
          {isError && <LoadingIndicator isError size={24} />}
          {isLoading && !isError ? (
            <SkeletonTable rowsCount={pagination.rowsPerPage} columnsCount={SKELETON_COLUMNS_COUNT} />
          ) : null}
          {!isLoading && !isError ? (
            <div className="mb-auto max-h-[75vh] w-full shrink overflow-y-auto">
              {isEmpty(inboxItems) ? (
                <div className="flex w-full justify-center py-32">
                  <Text variant={Text.VARIANTS.LG} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                    No records found
                  </Text>
                </div>
              ) : (
                <FnolAiInboxTable
                  rows={inboxItems}
                  onRowClick={toggleSelectedInboxItem}
                  selectedItemId={selectedInboxItem?.id}
                  withClaimColumn={inboxType === 'done'}
                  paginationProps={paginationProps}
                  onSortByColumn={handleSort}
                  defaultSortColumn={sortByColumn.id}
                  defaultSortOrder={sortByColumn.order}
                  pagination={pagination}
                  onRefresh={reloadInboxItems}
                  isLoading={isLoading}
                  inboxType={inboxType}
                />
              )}
            </div>
          ) : null}
        </>
      </Panel>

      {selectedInboxItem && (
        <>
          <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
          <Panel
            id="communication-display"
            className="relative max-h-[75vh] w-full"
            style={{ overflow: 'auto' }}
            minSize={35}
            maxSize={80}
          >
            <div className="flex h-full flex-col justify-between">
              <div>
                <div>
                  <ViewCommunicationCardContainer
                    key={selectedInboxItem.email_communication_id}
                    communicationId={selectedInboxItem.email_communication_id}
                    onClose={onCloseCommunicationCard}
                    onUpdate={refreshInboxItems} // Refresh inbox when communication status changes
                    displayAttachClaim={showAttachToClaim}
                    isDialog={showAttachToClaim}
                    onAttach={handleAttachToExistingClaim}
                    hideEmailCommunicationAction={true} // NOTE: FNOL inbox does not support reply/forward
                    BannerBelowHeader={
                      <CliveConditionalDisplay
                        subOrganizationId={selectedInboxItem.effective_sub_org_id}
                        displayClive={inboxType === 'in-queue' && shouldDisplayCliveForRow(selectedInboxItem)}
                      >
                        <FnolInboxItemAiAnalysisBanner
                          selectedInboxItem={selectedInboxItem}
                          onAttachToExistingClaim={handleAttachToExistingClaim}
                          onNewLoss={onCreateNewLoss}
                          onPolicySelection={onPolicySelection}
                          onRetry={onRetryProcessing}
                          onTakeLossManually={handleTakeLossManually}
                          onProcessingFinished={refreshInboxItemsAndSelection}
                        />
                      </CliveConditionalDisplay>
                    }
                    externalActionsToAdd={getMenuActions()}
                  />
                </div>
              </div>
            </div>
          </Panel>
        </>
      )}
      {!!editPolicyInboxItem && (
        <PolicySelectionCard
          policyNumber={editPolicyInboxItem?.policy?.policy_number}
          inboxItemId={editPolicyInboxItem.id}
          onCancel={onPolicySelectionCancel}
          onSubmit={handlePolicyChange}
          viewOnly={DISABLED_STATUSES_SET.has(editPolicyInboxItem.status)}
          shouldShowChangePolicyMessage={editPolicyInboxItem.is_clive_enabled_for_incident_configuration === true}
          constSearchValues={policySearchValues}
          onCloseWithRefresh={onPolicySelectionCancelWithRefresh}
          withClive={fnolWithClive}
          // CASE 2: Enable "Take Loss Without Policy" button in policy search modal when policy API is enabled.
          // The truthiness of this prop controls button visibility; the actual click handler is internal to PolicySelectionCard.
          showTakeLossWithoutPolicy={isPolicyAPIEnabled}
        />
      )}
      {showReassignInboxDialog && selectedInboxItem && (
        <ReassignInboxDialog
          inboxItemId={selectedInboxItem.id}
          onClose={() => setShowReassignInboxDialog(false)}
          onReassigned={onInboxReassigned}
        />
      )}
      {relatedClaims.length > 0 && (
        <ClaimSearchResults
          title="Existing claims for selected policy"
          searchResults={relatedClaims}
          onCancel={handleCancelDuplicateCheck}
          onClaimLinkClick={handleCancelDuplicateCheck}
          hideClaimType
          additionalComponent={
            <div className="flex justify-end p-16">
              <Button onClick={handleProceedDespiteDuplicates} variant="contained" color="primary">
                Create FNOL
              </Button>
            </div>
          }
        />
      )}
      {isCreatingFnol ? <LoadingDialog isError={false} track="Create FNOL from inbox" /> : null}
    </PanelGroup>
  );
};
